import React, { useEffect, useState } from 'react';
import axios from '../axiosConfig';
import ClientForm from './ClientForm';
import ClientDetailModal from './ClientDetailModal';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get('/clients/');
        const data = response.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setClients(list);
      } catch (err) {
        console.error('❌ Error fetching clients:', err);
        setError('Failed to load clients. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/clients/${id}/`);
      setClients((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('❌ Delete failed:', err);
      setError('Failed to delete client.');
    }
  };

  const handleAddClient = (newClient) => {
    setClients((prev) => [newClient, ...prev]);
  };

  const handleUpdateClient = (updatedClient) => {
    setClients((prev) =>
      prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
    );
    setEditingClient(null);
  };

  const filteredClients = clients.filter((client) => {
    const term = searchTerm.toLowerCase();
    return (
      client.name?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      (client.company || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-3xl mx-auto">
      {/* Form + Search bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <ClientForm
          onAdd={handleAddClient}
          selectedClient={editingClient}
          onUpdate={handleUpdateClient}
          onCancelEdit={() => setEditingClient(null)}
        />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded"
        />
      </div>

      <h2 className="text-2xl font-bold mb-4 text-[#F95F53]">Clients</h2>

      {loading && <p className="text-gray-500">Loading clients...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && filteredClients.length === 0 && (
        <p className="text-gray-600 italic">No clients found. Try adding one.</p>
      )}

      <ul className="space-y-4">
        {filteredClients.map((client, index) => (
          <li
            key={client.id || `${client.email}-${index}`}
            className="flex justify-between items-center bg-gray-100 px-4 py-3 rounded-md border"
          >
            <div>
              <button
                onClick={() => setSelectedClient(client)}
                className="font-semibold text-[#111827] hover:underline focus:outline-none"
              >
                {client.name}
              </button>
              <p className="text-sm text-gray-600">
                {client.email || <em>No email provided</em>}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setEditingClient(client)}
                className="text-sm bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(client.id)}
                className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
};

export default ClientList;